import { Children, type ReactNode } from "react";
import { Head } from "zudoku/components";
import {
  HOSTING,
  HOSTING_IDS,
  type HostingId,
  PLANS,
  type PlanId,
  PRODUCTS,
  type ProductId,
  hostingSummary,
} from "../applies-to/scope.js";

const splitIds = <T extends string>(value: string | undefined, known: T[]) =>
  (value ?? "").split(/\s+/).filter((id): id is T => known.includes(id as T));

const PRODUCT_IDS = Object.keys(PRODUCTS) as ProductId[];

const Kicker = ({ children }: { children: ReactNode }) => (
  <span className="text-[11px] font-semibold uppercase leading-6 tracking-wider text-muted-foreground">
    {children}
  </span>
);

type HostingState = "supported" | "differs" | "unavailable";

const HOSTING_ICON_CLASS: Record<HostingState, string> = {
  supported: "text-emerald-500",
  differs: "text-amber-500",
  unavailable: "text-zinc-300 dark:text-zinc-600",
};

const HostingIcon = ({ state }: { state: HostingState }) => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    className={`shrink-0 self-center ${HOSTING_ICON_CLASS[state]}`}
  >
    <circle cx="12" cy="12" r="10" fill="currentColor" />
    {state === "unavailable" ? (
      <path d="M8 12h8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
    ) : (
      <path
        d="M7.5 12.5l3 3 6-6.5"
        stroke="#fff"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const PRODUCT_CHIP_CLASS: Record<ProductId, string> = {
  gateway:
    "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200",
  "developer-portal":
    "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200",
};

const PRODUCT_DOT_CLASS: Record<ProductId, string> = {
  gateway: "bg-violet-500",
  "developer-portal": "bg-sky-500",
};

const ProductChip = ({ product }: { product: ProductId }) => (
  <span
    className={`inline-flex h-6 items-baseline gap-1.5 rounded-full px-2.5 text-[13px] font-medium ${PRODUCT_CHIP_CLASS[product]}`}
  >
    <span
      aria-hidden="true"
      className={`size-1.5 self-center rounded-full ${PRODUCT_DOT_CLASS[product]}`}
    />
    {PRODUCTS[product].label}
  </span>
);

const DiffersTag = () => (
  <span className="rounded border border-amber-200 bg-amber-50 px-1.5 text-[11px] font-semibold uppercase leading-4 tracking-wider text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
    Differs
  </span>
);

const NotAvailableTag = () => (
  <span className="rounded border border-zinc-200 bg-zinc-100 px-1.5 text-[11px] font-semibold uppercase leading-4 tracking-wider text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
    Not available
  </span>
);

const PlanTag = ({ plan }: { plan: PlanId }) => (
  <span className="rounded border px-1.5 text-[11px] font-semibold uppercase leading-4 tracking-wider text-muted-foreground">
    {PLANS[plan].label}
  </span>
);

const ScopeHead = ({
  products,
  hosting,
  differs,
  plan,
  title,
  description,
}: {
  products: ProductId[];
  hosting: HostingId[] | null;
  differs: HostingId[];
  plan?: PlanId;
  title?: string;
  description?: string;
}) => {
  const productNames = products.map((id) => `Zuplo ${PRODUCTS[id].label}`);
  const audience = hosting
    ? `Zuplo ${hostingSummary(hosting)} customers`
    : undefined;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    ...(title ? { headline: title } : {}),
    ...(description ? { description } : {}),
    ...(productNames.length > 0
      ? {
          about: productNames.map((name) => ({
            "@type": "SoftwareApplication",
            name,
            applicationCategory: "DeveloperApplication",
          })),
        }
      : {}),
    ...(audience
      ? { audience: { "@type": "Audience", audienceType: audience } }
      : {}),
    keywords: [...products, ...(hosting ?? []), ...(plan ? [plan] : [])].join(
      ", ",
    ),
    isPartOf: {
      "@type": "WebSite",
      name: "Zuplo Docs",
      url: "https://zuplo.com/docs",
    },
  };

  return (
    <Head>
      {products.length > 0 && (
        <meta name="zuplo:product" content={products.join(" ")} />
      )}
      {products.length > 0 && (
        <meta name="inkeep:product" content={products.join(" ")} />
      )}
      {hosting && <meta name="zuplo:hosting" content={hosting.join(" ")} />}
      {hosting && <meta name="inkeep:hosting" content={hosting.join(" ")} />}
      {differs.length > 0 && (
        <meta name="zuplo:hosting-differs" content={differs.join(" ")} />
      )}
      {plan && <meta name="zuplo:plan" content={plan} />}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Head>
  );
};

const SectionMarker = ({ hosting }: { hosting: HostingId[] }) => (
  <p className="not-prose my-2">
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted py-0.5 pl-1.5 pr-2.5 text-xs font-medium text-muted-foreground">
      <HostingIcon state="supported" />
      <span className="sr-only">Applies to: </span>
      {hostingSummary(hosting)}
    </span>
  </p>
);

/**
 * "Applies to" banner. The `remarkAppliesTo` plugin injects the page variant
 * (`page`) under every page title from its `applies_to`
 * frontmatter. Authors write the section variant by hand:
 *
 * ```mdx
 * <AppliesTo hosting="managed-dedicated" />
 * ```
 */
export const AppliesTo = ({
  page,
  product,
  hosting: hostingAttribute,
  differs: differsAttribute,
  plan,
  title,
  description,
  children,
}: {
  page?: boolean;
  product?: string;
  hosting?: string;
  differs?: string;
  plan?: PlanId;
  title?: string;
  description?: string;
  children?: ReactNode;
}) => {
  const hostingIds = splitIds(hostingAttribute, HOSTING_IDS);

  if (!page) {
    return hostingIds.length > 0 ? (
      <SectionMarker hosting={hostingIds} />
    ) : null;
  }

  const products = splitIds(product, PRODUCT_IDS);
  const hosting = hostingAttribute === undefined ? null : hostingIds;
  const differs = splitIds(differsAttribute, HOSTING_IDS);
  const notes = Children.toArray(children);

  return (
    <>
      <ScopeHead
        products={products}
        hosting={hosting}
        differs={differs}
        plan={plan}
        title={title}
        description={description}
      />
      <section
        aria-label="Applies to"
        data-pagefind-ignore
        className="not-prose mb-6 flex flex-col gap-3 rounded-xl border bg-muted/40 px-5 py-4 text-sm"
      >
        <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-2 text-[13px] leading-6">
          {products.length > 0 && (
            <>
              <dt>
                <Kicker>Applies to</Kicker>
              </dt>
              <dd className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
                {products.map((id, index) => (
                  <span key={id} className="inline-flex items-baseline gap-2.5">
                    {index > 0 && (
                      <span className="text-muted-foreground">+</span>
                    )}
                    <ProductChip product={id} />
                  </span>
                ))}
                {products.length === 1 && (
                  <span className="text-muted-foreground">
                    {PRODUCTS[products[0]!].summary}
                  </span>
                )}
                {plan && <PlanTag plan={plan} />}
              </dd>
            </>
          )}
          {products.length === 0 && plan && (
            <>
              <dt>
                <Kicker>Plan</Kicker>
              </dt>
              <dd className="flex items-center">
                <PlanTag plan={plan} />
              </dd>
            </>
          )}
          {hosting && (
            <>
              <dt>
                <Kicker>Hosting</Kicker>
              </dt>
              <dd className="flex flex-col gap-y-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4">
                {HOSTING_IDS.map((id) => {
                  const state: HostingState = !hosting.includes(id)
                    ? "unavailable"
                    : differs.includes(id)
                      ? "differs"
                      : "supported";
                  return (
                    <span
                      key={id}
                      className={`inline-flex items-baseline gap-1.5 font-medium ${state === "unavailable" ? "text-muted-foreground" : ""}`}
                    >
                      <HostingIcon state={state} />
                      {HOSTING[id].label}
                      {state === "differs" && <DiffersTag />}
                      {state === "unavailable" && <NotAvailableTag />}
                    </span>
                  );
                })}
              </dd>
            </>
          )}
        </dl>

        {notes.length > 0 && (
          <details
            open={notes.length === 1}
            className="group border-t pt-3 [&_summary::-webkit-details-marker]:hidden"
          >
            <summary className="inline-flex min-h-7 cursor-pointer list-none items-center gap-2 text-[13px] font-medium">
              <svg
                aria-hidden="true"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="transition-transform group-open:rotate-90"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
              {notes.length === 1
                ? "1 difference by hosting option"
                : `${notes.length} differences by hosting option`}
            </summary>
            <div className="mt-2 grid grid-cols-1 items-baseline gap-x-4 gap-y-1 pl-[22px] text-[13px] leading-5 sm:grid-cols-[max-content_1fr] sm:gap-y-2.5">
              {notes}
            </div>
          </details>
        )}
      </section>
    </>
  );
};

/** One `differs` note inside the page banner. */
export const AppliesToNote = ({
  hosting,
  children,
}: {
  hosting: HostingId;
  children?: ReactNode;
}) => (
  <div className="contents">
    <span className="inline-flex items-baseline gap-1.5 font-medium">
      <HostingIcon state="differs" />
      {HOSTING[hosting]?.label ?? hosting}
    </span>
    <div className="mb-2 text-muted-foreground last:mb-0 sm:mb-0 [&_a]:text-foreground [&_a]:underline [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_p]:m-0">
      {children}
    </div>
  </div>
);
